import { useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  CAPGUARD_PORT_TYPES,
  COMPANY_ALLOWED_PORT_TYPES,
  portfolioTypeOptions,
} from "../OrderForm.options";
import { OrderFormData } from "../OrderForm.schema";
import { Portfolio } from "@/api/types";

export const NEW_PORTFOLIO_ID = "NEW";

type Props = {
  portfolios: Portfolio[];
  allPortfolios: Portfolio[];
  isCompany?: boolean;
};

const ASK_IPS_TYPES = new Set(["22", "23", "Nordnet ASK", "Nordnet IPS"]);

function formatCurrency(val: number) {
  if (isNaN(val)) return "0";
  return new Intl.NumberFormat("no-NO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
}

export function PortfolioSelector({
  portfolios,
  allPortfolios,
  isCompany = false,
}: Props) {
  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext<OrderFormData>();

  const portfolioId = useWatch({ name: "dpPortfolioId" });
  const portType = useWatch({ name: "dpPortType" });
  const secValue = useWatch({ name: "dpSumInsValue" });
  const cashValue = useWatch({ name: "dpSumAvCash" });

  const selected = portfolios.find((p) => p.id === Number(portfolioId));
  const isNew = portfolioId === NEW_PORTFOLIO_ID;

  const prevPortfolioIdRef = useRef(portfolioId);

  // Auto-fill portType from selected portfolio
  useEffect(() => {
    if (portfolioId === prevPortfolioIdRef.current) return; // unchanged — skip (handles editing pre-fill)
    prevPortfolioIdRef.current = portfolioId;
    if (selected) {
      setValue("dpPortType", selected.typeCode ?? "", { shouldValidate: true });
      setValue(
        "dpSumInsValue",
        selected.portfolioReport.positionMarketValue ?? 0
      );
      setValue("dpPortfolioName", selected.name);
      setValue("dpSumAvCash", selected.portfolioReport.accountBalance ?? 0);
    } else if (isNew) {
      setValue("dpPortType", "");
      setValue("dpPortfolioName", "New Portfolio");
      setValue("dpSumInsValue", 0);
      setValue("dpSumAvCash", 0);
    }
  }, [portfolioId, selected, isNew, setValue]);

  return (
    <div className="space-y-3">
      {/* Portfolio */}
      <div className="grid grid-cols-[180px_1fr] items-center gap-3">
        <label className="text-sm font-medium text-right text-gray-600 dark:text-gray-400">
          Portfolio:
        </label>
        <select
          {...register("dpPortfolioId")}
          className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">-- Select --</option>
          <option value={NEW_PORTFOLIO_ID}>New Portfolio</option>
          <option disabled>──────────</option>
          {portfolios
            .filter(
              (p) => !isCompany || COMPANY_ALLOWED_PORT_TYPES.has(p.typeCode)
            )
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.shortName} | {p.name}
              </option>
            ))}
        </select>
        {errors.dpPortfolioId && (
          <p className="col-start-2 text-xs text-red-500">
            {errors.dpPortfolioId.message}
          </p>
        )}
      </div>

      {/* Portfolio Type */}
      <div className="grid grid-cols-[180px_1fr] items-center gap-3">
        <label className="text-sm font-medium text-right text-gray-600 dark:text-gray-400">
          Portfolio Type:
        </label>
        {isNew ? (
          <select
            {...register("dpPortType")}
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">-- Select type --</option>
            {portfolioTypeOptions
              .filter(
                (opt) => !isCompany || COMPANY_ALLOWED_PORT_TYPES.has(opt.value)
              )
              .map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
          </select>
        ) : (
          <input
            value={
              portfolioTypeOptions.find((o) => o.value === portType)?.label ||
              portType ||
              ""
            }
            disabled
            className="w-full rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-500"
          />
        )}
      </div>

      {/* Value of positions */}
      <div className="grid grid-cols-[180px_1fr] items-center gap-3">
        <label className="text-sm font-medium text-right text-gray-600 dark:text-gray-400">
          Value positions:
        </label>
        <input
          {...register("dpSumInsValue")}
          value={`NOK ${formatCurrency(secValue)}`}
          disabled
          className="w-full rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-500"
        />
      </div>

      {/* Available cash */}
      <div className="grid grid-cols-[180px_1fr] items-center gap-3">
        <label className="text-sm font-medium text-right text-gray-600 dark:text-gray-400">
          Available cash:
        </label>
        <input
          {...register("dpSumAvCash")}
          value={`NOK ${formatCurrency(cashValue)}`}
          disabled
          className="w-full rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-500"
        />
      </div>
    </div>
  );
}
